import {
  pipe,
  split,
  head,
  divide,
  __,
  pathOr,
  path,
  isEmpty,
  isNil,
  find,
} from 'ramda'

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

// used to get 1st, 2nd, 3rd, etc.
export const getSimpleOrdinality = n => {
  if (n === 11 || n === 12 || n === 13) return n + 'th'
  if (n % 10 === 1) return n + 'st'
  if (n % 10 === 2) return n + 'nd'
  if (n % 10 === 3) return n + 'rd'
  return n + 'th'
}

/**
 * allows us to add our own timezone onto datetime strings
 * @param {string} dateString
 * @param {string} time
 * @returns {string} datetime
 */
export const adjustTZOnDate = (dateString, time) => {
  if (!dateString || !time)
    throw new Error('Arguments must not be null or undefined.')

  // must be in format YYYY-MM-DDTHH:MM:SS:sssZ
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(dateString))
    throw new Error('Must be a valid ISO date string or timestamp.')

  return pipe(
    // split the datetime string at the timezone marker
    split('T'),
    // take just the YYYY-MM-DD portion
    head,
    // add to the string the timezone marker that's almost the
    // next day for the endDate or sets it to 00 for the start date
    // so that backend returns all the transactions on that date and not just
    // those from 8am GMT time which was what ISOString() was setting it to
    str => str.concat(time),
  )(dateString)
}

/**
 * will get the difference of two dates discarding timezone information
 * and return a whole number (positive if d1 is before d2, negative otherwise)
 * will also throw errors if the dates passed in don't meet requirements
 * @param {string} d1 datetime string or JS date time object
 * @param {string} d2 datetime string or JS date time object
 * @returns {integer} a whole number
 */
export const getDateDifferenceInDays = (d1, d2) => {
  if (!d1 || !d2) throw new Error('Must not be null or undefined.')

  // make d1 and d2 into js date objects
  const dt1 = new Date(d1)
  const dt2 = new Date(d2)

  if (isNaN(dt1.getTime()) || isNaN(dt2.getTime()))
    throw new Error('Must be a valid date string or timestamp.')

  // convert to UTC, discard time and time-zone
  // information and divide by milliseconds per day
  return Math.floor(
    (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
      Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
      (1000 * 60 * 60 * 24),
  )
}

/**
 * will get the year difference of two dates discarding timezone information
 * and return a whole number for a year (positive if d1 is before d2, negative otherwise)
 * will also throw errors if the dates pased in don't meet requirements
 *
 * @param {string} d1 datetime string or JS date time object
 * @param {string} d2 datetime string or JS date time object
 * @returns {integer} a whole number describing the year difference
 */

export const getDateDifferenceInYears = (d1, d2) => {
  if (!d1 || !d2) throw new Error('Must not be null or undefined.')

  return pipe(
    // get the number of days difference
    getDateDifferenceInDays,
    // divide by 365 to convert to years
    divide(__, 365),
    // round down in order to only get the whole number
    Math.floor,
  )(d1, d2)
}

// use formatDate to format a date IN LOCAL TIME
export const formatDate = (format, date) => {
  const d =
    typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  if (d === undefined) return null
  if (isNaN(d.valueOf())) return null

  const yr = d.getFullYear() + ''
  const mo = d.getMonth()
  const mon = mo + 1 // for when 1 should represent January
  const dt = d.getDate()
  const dy = d.getDay()
  const hr = d.getHours()
  const hrs = hr > 12 ? hr - 12 : hr // for 12 hour time
  const mm = d.getMinutes()
  const ss = d.getSeconds()
  const timeZone = d
    .toLocaleTimeString('en-us', { timeZoneName: 'short' })
    .split(' ')[2]

  // longer versions must come before shorter versions
  // so that YYYY doesn't turn into 2 YY's. Also do the
  // am/pm thing first because we don't want months or
  // days to have their a and A letters hijacked.
  return format
    .replace(/A/g, hr < 12 ? 'AM' : 'PM')
    .replace(/a/g, hr < 12 ? 'am' : 'pm')
    .replace(/YYYY/g, yr)
    .replace(/YY/g, yr.substr(2))
    .replace(/MMMM/g, months[mo])
    .replace(/MMM/g, months[mo].substr(0, 3))
    .replace(/MM/g, mon < 10 ? `0${mon}` : mon)
    .replace(/DDDD/g, days[dy])
    .replace(/DDD/g, days[dy].substr(0, 3))
    .replace(/DD/g, dt < 10 ? `0${dt}` : dt)
    .replace(/DL/g, dt) // single D was fucking up December
    .replace(/HH/g, hr < 10 ? `0${hr}` : hr)
    .replace(/hh/g, hrs < 10 ? `0${hrs}` : hrs)
    .replace(/mm/g, mm < 10 ? `0${mm}` : mm)
    .replace(/ss/g, ss < 10 ? `0${ss}` : ss)
    .replace(/tz/g, timeZone)
}

// use formatDateUTC to format a date IN UTC TIME
export const formatDateUTC = (format, date) => {
  const d =
    typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  if (d === undefined) return null
  if (isNaN(d.valueOf())) return null

  const yr = d.getUTCFullYear() + ''
  const mo = d.getUTCMonth()
  const mon = mo + 1 // for when 1 should represent January
  const dt = d.getUTCDate()
  const dy = d.getUTCDay()
  const hr = d.getUTCHours()
  const hrs = hr > 12 ? hr - 12 : hr // for 12 hour time
  const mm = d.getUTCMinutes()
  const ss = d.getUTCSeconds()
  const timeZone = d
    .toLocaleTimeString('en-us', { timeZoneName: 'short' })
    .split(' ')[2]

  // longer versions must come before shorter versions
  // so that YYYY doesn't turn into 2 YY's. Also do the
  // am/pm thing first because we don't want months or
  // days to have their a and A letters hijacked.
  return format
    .replace(/A/g, hr < 12 ? 'AM' : 'PM')
    .replace(/a/g, hr < 12 ? 'am' : 'pm')
    .replace(/YYYY/g, yr)
    .replace(/YY/g, yr.substr(2))
    .replace(/MMMM/g, months[mo])
    .replace(/MMM/g, months[mo].substr(0, 3))
    .replace(/MM/g, mon < 10 ? `0${mon}` : mon)
    .replace(/DDDD/g, days[dy])
    .replace(/DDD/g, days[dy].substr(0, 3))
    .replace(/DD/g, dt < 10 ? `0${dt}` : dt)
    .replace(/DL/g, dt) // single D was fucking up December
    .replace(/HH/g, hr < 10 ? `0${hr}` : hr)
    .replace(/hh/g, hrs < 10 ? `0${hrs}` : hrs)
    .replace(/mm/g, mm < 10 ? `0${mm}` : mm)
    .replace(/ss/g, ss < 10 ? `0${ss}` : ss)
    .replace(/tz/g, timeZone)
}

// handy preset formatting functions
export const getFullDateDisplay = d => formatDate('MMMM DL, YYYY', d)
export const getFullDateShortMonthDisplay = d => formatDate('MMM DL, YYYY', d)

export const getFullDateTimeDisplay = d =>
  formatDate('MM/DD/YYYY, HH:mm:ssa', d)
export const getShortDateDisplay = d => formatDate('MM/DD/YY', d)
export const getShortDateFullYearDisplay = d => formatDate('MM/DD/YYYY', d)
export const getShortTimeDisplay = d => formatDate('hh:mma', d)
export const getMonthDisplay = d => formatDate('MMM', d)
export const getDayDisplay = d => formatDate('DD', d)
export const getTimeZoneDisplay = d => formatDate('tz', d)
export const getDateOfBirthDisplay = dob => {
  const d =
    typeof dob === 'string' || typeof dob === 'number' ? new Date(dob) : dob
  if (d === undefined) return null
  if (isNaN(d.valueOf())) return null

  const year = d.getUTCFullYear() + ''
  const monthIndex = d.getUTCMonth()
  const date = d.getUTCDate()

  // Use the client timezone to display birthday
  return formatDate('MM/DD/YYYY', new Date(year, monthIndex, date))
}

/**
 * determines if date is in a valid format
 * @param {string || number} date datetime string or JS date time object
 * @returns {boolean} returns if this is a valid date
 */

export const isValidDate = date => {
  const d =
    typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  if (d === undefined || isNaN(d.valueOf())) return false
  return true
}

// Useful regex's for string testing

export const ISODateRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/gm

/**
 * takes a date and converts it to the policy locale time
 * regardless of where the browser/mobile device is at the time
 * @param {string} date | ISO datetime string
 * @param {string} format | format you'd like the date to be in
 * @param {string} tzString | ICANN tz db name
 * @returns {string}
 */
export const convertToPolicyTZ = ({ date, format, tzString }) => {
  if (!date || !format || !tzString)
    throw new Error('Arguments must not be null or undefined.')

  // must be in format YYYY-MM-DDTHH:MM:SS:sssZ
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(date))
    throw new Error('Must be a valid ISO date string or timestamp.')

  const converted = new Date(date).toLocaleString('en-US', {
    timeZone: tzString,
  })

  return formatDate(format, converted)
}

/**
 * pass in a date and this will check if today is past the date
 * @param {string} date | string representation of a date or datetime string
 * @returns {boolean}
 */
export const isDateOverdue = date => {
  const dueDate = new Date(date)

  if (isNaN(dueDate.getDate())) return false

  const today = new Date()
  return today > dueDate
}

/**
 * Goes through tha accruable dates and sees if it falls within the range
 * of the current date, if it does assign it as the initial start and end date.
 * If it doesnt simply assigns it to the first value in the list of dates
 * @param {string} result | string representation of the response from api
 * @param {string} memberUuid | string representation of the member uuid
 * @returns {object}
 */

export const getAccruableDates = (result, memberUuid) => {
  let startDate = ''
  let endDate = ''
  const currentDate = Date.parse(formatDateUTC('MM/DD/YYYY', new Date()))
  path(['payload', 'accrualPeriods'], result).map(date => {
    if (
      currentDate >= Date.parse(formatDateUTC('MM/DD/YYYY', date.startDate)) &&
      currentDate <= Date.parse(formatDateUTC('MM/DD/YYYY', date.endDate))
    ) {
      startDate = date.startDate
      endDate = date.endDate
    }
    if (isEmpty(startDate)) {
      startDate = path(['payload', 'accrualPeriods', 0, 'startDate'], result)
      endDate = path(['payload', 'accrualPeriods', 0, 'endDate'], result)
    }
  })

  return { startDate, endDate }
}
